import { useCallback, useEffect, useMemo, useState } from "react";
import { TableColumnsType, TableProps } from "antd";
import { useAppSelector } from "redux/hooks";
import { useOutletContext } from "react-router-dom";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { UsageLayoutOutletContext } from "api/interfaces/subscription.interface";
import {
  BillToDateInvoiceMeta,
  BillToDatePresenterProps,
  ChannelInvoiceDto,
  StorageInvoiceDto,
  UsageBreakDownDto,
} from "api/interfaces/planInterface.interface";
import { FiCloud, FiPlusSquare } from "react-icons/fi";
import BillToDatePresenter from "./BillToDatePresenter";
import BillToDateEmptyState from "./BillToDateEmptyState";
import * as s from "./BillToDateStyled";
import { getPlanInvoice } from "api/planAPI";
import { useQuery } from "react-query";
import { formatNumber } from "utils/functions";
import {
  renderEventChange,
  renderEventType,
} from "pages/subscription/usage/eventToHistory/eventToHistoryCommon";
import { getAccountTimeFormatDate } from "utils/timeUtil";
import { isYearlyBillingCycle } from "utils/priceUtils";

const getDescriptionFromKey = (key: string) => {
  const lower = key.toLowerCase();
  if (lower.includes("channel"))
    return { title: "Channel Usage (weighted avg)", icon: <FiPlusSquare /> };
  if (lower.includes("storage"))
    return { title: "Cloud Storage (prorated)", icon: <FiCloud /> };
  return { title: key, icon: <FiPlusSquare /> };
};

export default function BillToDateContainer() {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { setButtons, selectedGraphDate } =
    useOutletContext<UsageLayoutOutletContext>();
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const { data } = useQuery(
    ["getPlanInvoice", selectedAccount.accountId, selectedGraphDate],
    () =>
      getPlanInvoice({
        accountId: selectedAccount.accountId,
        payload: { billingDate: selectedGraphDate },
      }),
    {
      enabled: !!selectedAccount.accountId && !!selectedGraphDate,
      retry: 0,
    }
  );

  const invoiceMeta = useMemo<BillToDateInvoiceMeta>(
    () => ({
      customer: data?.result.customer ?? "",
      billingDate: data?.result.billingDate ?? "",
      cycleStart: data?.result.cycleStart ?? "",
      cycleEnd: data?.result.cycleEnd ?? "",
    }),
    [data?.result]
  );

  const subscriptionColumns = useMemo<TableColumnsType<UsageBreakDownDto>>(
    () => [
      {
        title: "Description",
        dataIndex: "key",
        render: (key: string, row: UsageBreakDownDto) => {
          const { title, icon } = getDescriptionFromKey(key);
          return (
            <s.DescriptionCell>
              <div className="icon">{icon}</div>
              <div className="text">
                <div className="title">{title}</div>
                <div className="subtitle">
                  {row.euCount} End {row.euCount === 1 ? "User" : "Users"} - sum
                  of user averages
                </div>
              </div>
            </s.DescriptionCell>
          );
        },
      },
      {
        title: "Qty",
        dataIndex: "qty",
        render: (value: number) => `${formatNumber(value)}`,
        width: 140,
      },
      {
        title: "Unit Price",
        dataIndex: "unitPrice",
        render: (value: number) =>
          `$${formatNumber(value, { fractionDigits: 2 })}`,
        width: 160,
        align: "right",
      },
      {
        title: "Amount",
        dataIndex: "amount",
        render: (value: number) =>
          `$${formatNumber(value, { fractionDigits: 2 })}`,
        width: 140,
        align: "right",
      },
    ],
    []
  );

  const usageColumns = useMemo<TableColumnsType<ChannelInvoiceDto>>(
    () => [
      {
        title: "End User",
        dataIndex: "orgName",
        render: (orgName: string) => (
          <s.EndUserCell>
            <span className="badge">EU</span>
            <span className="name">{orgName}</span>
          </s.EndUserCell>
        ),
      },
      {
        title: "",
        dataIndex: "activated",
        render: (value: number) => (
          <s.ActivatedChannelsCell>
            {formatNumber(value)} {value > 1 ? "Channels" : "Channel"} Activated
          </s.ActivatedChannelsCell>
        ),
        width: 220,
      },
      {
        title: "Average Channels",
        dataIndex: "average",
        width: 180,
        render: (value: number) => formatNumber(value),
      },
      {
        title: "Amount",
        dataIndex: "amount",
        render: (value: number) =>
          `$${formatNumber(value, { fractionDigits: 2 })}`,
        width: 140,
        align: "right",
        hidden: isYearlyBillingCycle(selectedAccount.cycle),
      },
    ],
    []
  );

  const storageColumns = useMemo<TableColumnsType<StorageInvoiceDto>>(
    () => [
      {
        title: "End User",
        dataIndex: "orgName",
        render: (orgName: string) => (
          <s.EndUserCell>
            <span className="badge">EU</span>
            <span className="name">{orgName}</span>
          </s.EndUserCell>
        ),
      },
      {
        title: "TB Purchased",
        dataIndex: "purchased",
        width: 180,
        render: (value: number) => formatNumber(value),
      },
      {
        title: "Active Since",
        dataIndex: "since",
        render: (since: string) => {
          if (!since) return "-";
          return getAccountTimeFormatDate(since, selectedAccount);
        },
        width: 180,
      },
      {
        title: "Pro-Rated Storage",
        dataIndex: "proRated",
        render: (value: number) =>
          `${formatNumber(value, { fractionDigits: 1 })}`,
        width: 180,
      },
      {
        title: "Amount",
        dataIndex: "amount",
        render: (value: number) =>
          `$${formatNumber(value, { fractionDigits: 2 })}`,
        width: 140,
        align: "right",
        hidden: isYearlyBillingCycle(selectedAccount.cycle),
      },
    ],
    []
  );

  const usageRows: ChannelInvoiceDto[] = useMemo(() => {
    if (data?.error === 0 && data?.result?.channels) {
      return data.result.channels;
    }
    return [];
  }, [data]);
  const isAllExpanded = expandedRowKeys.length === usageRows.length;
  const allExpandableKeys = useMemo(
    () => usageRows.map((row) => row.orgName),
    [usageRows]
  );
  const isAbleExpand = useMemo(
    () => usageRows.some((row) => row.history?.length > 0),
    [usageRows]
  );

  const subscriptionTableProps = useMemo<TableProps<UsageBreakDownDto>>(
    () => ({
      rowKey: "key",
      columns: subscriptionColumns,
      dataSource: data?.result.usageBreakDown ?? [],
    }),
    [subscriptionColumns, data?.result.usageBreakDown]
  );

  const usageTableProps = useMemo<TableProps<ChannelInvoiceDto>>(
    () => ({
      rowKey: "orgName",
      columns: usageColumns,
      dataSource: usageRows,
      locale: { emptyText: <BillToDateEmptyState variant="channel" /> },
      expandable: {
        expandedRowKeys,
        onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
        rowExpandable: (record) => record.history && record.history.length > 0,
        expandedRowRender: (record) => (
          <s.ExpandedDetail>
            <div className="title">Event History</div>
            {record.history.map((h) => (
              <div className="row" key={h.get_id}>
                <span className="date">
                  {getAccountTimeFormatDate(h.usageDate, selectedAccount)}
                </span>
                <span />
                <span className="status">
                  {renderEventType(h.planEventType)}
                </span>
                <span className="channels">{renderEventChange(h)}</span>
              </div>
            ))}
          </s.ExpandedDetail>
        ),
      },
    }),
    [expandedRowKeys, usageColumns, usageRows]
  );

  const storageTableProps = useMemo<TableProps<StorageInvoiceDto>>(
    () => ({
      rowKey: "orgName",
      columns: storageColumns,
      dataSource: data?.result.storage ?? [],
      locale: { emptyText: <BillToDateEmptyState variant="storage" /> },
    }),
    [storageColumns, data?.result.storage]
  );

  const onToggleExpandAll = useCallback(() => {
    setExpandedRowKeys(isAllExpanded ? [] : allExpandableKeys);
  }, [isAllExpanded, allExpandableKeys]);

  useEffect(() => {
    setButtons(null);
  }, [setButtons]);

  return (
    <BillToDatePresenter
      invoiceMeta={invoiceMeta}
      subscriptionTableProps={subscriptionTableProps}
      usageTableProps={usageTableProps}
      storageTableProps={storageTableProps}
      isAllExpanded={isAllExpanded}
      onToggleExpandAll={onToggleExpandAll}
      isAbleExpand={isAbleExpand}
    />
  );
}
